import { Input } from "@/components/ui/input";
import { AgencyBatchFilter, useAgencyBatches } from "./AgencyBatchFilter";

export const INBOX_SELECT_CLASS = "min-h-11 w-full rounded-md border border-border bg-background px-2 text-sm";
export interface InboxFiltersState {
  owner_query: string; status: string; ai_status: string; min_confidence: string;
  mismatch: string; source: string; uploaded_by_id: string; batch_id: string;
  uploaded_from: string; uploaded_to: string;
}
export const EMPTY_INBOX_FILTERS: InboxFiltersState = {
  owner_query: "", status: "pending", ai_status: "", min_confidence: "", mismatch: "", source: "",
  uploaded_by_id: "", batch_id: "", uploaded_from: "", uploaded_to: "",
};
export function inboxFilterParams(filters: InboxFiltersState, company: boolean, includeFamily = false) {
  const params = new URLSearchParams();
  (company ? ["firma", "gradiliste"] : ["radnik", ...(includeFamily ? ["obitelj"] : [])]).forEach((kind) => params.append("owner_kinds", kind));
  if (filters.status === "pending") ["novo", "predlozeno"].forEach((status) => params.append("statuses", status));
  else if (filters.status) params.append("statuses", filters.status);
  Object.entries(filters).forEach(([key, value]) => {
    if (!value || key === "status") return;
    if (key === "uploaded_from") params.set(key, new Date(`${value}T00:00:00`).toISOString());
    else if (key === "uploaded_to") params.set(key, new Date(`${value}T23:59:59.999`).toISOString());
    else params.set(key, value);
  });
  return params;
}

const FILTER_OPTIONS = {
  status: [["pending", "Novo i predloženo"], ["", "Svi statusi"], ["novo", "Novo"], ["predlozeno", "Predloženo"], ["potvrdeno", "Potvrđeno"], ["odbaceno", "Odbačeno"], ["duplikat", "Duplikat"]],
  ai_status: [["", "Svi"], ["none", "Bez AI obrade"], ["queued", "U obradi"], ["done", "Obrađeno"], ["failed", "Neuspješno"]],
  mismatch: [["", "Sve"], ["true", "S neslaganjem"], ["false", "Bez neslaganja"]],
  source: [["", "Svi izvori"], ["scanner", "Skener (povijesno)"], ["webdav", "Epson (WebDAV)"], ["drop", "Povlačenje"], ["picker", "Odabir datoteke"], ["camera", "Kamera"]],
} satisfies Record<string, [string, string][]>;

const FILTER_LABELS: Record<keyof InboxFiltersState, string> = {
  owner_query: "Nositelj", status: "Status", ai_status: "AI status", min_confidence: "Min. pouzdanost",
  mismatch: "Neslaganje", source: "Izvor", uploaded_by_id: "Unio", batch_id: "Uvoz paketa",
  uploaded_from: "Datum od", uploaded_to: "Datum do",
};

export function InboxFilterSummary({ value, uploaders }: {
  value: InboxFiltersState; uploaders: { id: string; name: string }[];
}) {
  const { batches } = useAgencyBatches(value.batch_id, !!value.batch_id);
  const active = (Object.keys(FILTER_LABELS) as (keyof InboxFiltersState)[]).filter((key) => value[key] !== "");
  function label(key: keyof InboxFiltersState) {
    const raw = value[key];
    if (key === "batch_id") return batches.find(batch => batch.id === raw)?.name ?? "Odabrani uvoz paketa";
    if (key in FILTER_OPTIONS) return FILTER_OPTIONS[key as keyof typeof FILTER_OPTIONS].find(([id]) => id === raw)?.[1] ?? raw;
    if (key === "uploaded_by_id") return uploaders.find(({ id }) => id === raw)?.name ?? raw;
    if (key === "min_confidence") return `${(Number(raw) * 100).toLocaleString("hr-HR", { maximumFractionDigits: 2 })} %`;
    if (key === "uploaded_from" || key === "uploaded_to") return raw.split("-").reverse().join(".") + ".";
    return raw;
  }
  return <ul aria-label="Aktivni filtri" className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-1 text-xs">
    {active.length ? active.map((key) => <li key={key} className="shrink-0 whitespace-nowrap rounded-full border bg-muted px-2 py-1">{FILTER_LABELS[key]}: {label(key)}</li>) : <li className="whitespace-nowrap text-muted-foreground">Nema aktivnih filtara</li>}
  </ul>;
}

export function InboxFilters({ value, onChange, uploaders, pendingOnly = false }: {
  value: InboxFiltersState; onChange: (next: InboxFiltersState) => void; uploaders: { id: string; name: string }[]; pendingOnly?: boolean;
}) {
  const change = (key: keyof InboxFiltersState, next: string) => onChange({ ...value, [key]: next });
  const select = (key: keyof InboxFiltersState, label: string, options: [string, string][]) => <label className="space-y-1 text-sm">{label}<select className={INBOX_SELECT_CLASS} value={value[key]} onChange={(event) => change(key, event.target.value)}>{options.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>;
  return <div className="grid grid-cols-2 gap-3">
    <label className="space-y-1 text-sm">Nositelj<Input value={value.owner_query} onChange={(event) => change("owner_query", event.target.value)} placeholder="Radnik, firma ili gradilište" /></label>
    {!pendingOnly && select("status", "Status", FILTER_OPTIONS.status)}
    {select("ai_status", "AI status", FILTER_OPTIONS.ai_status)}
    <label className="space-y-1 text-sm">Min. pouzdanost (0–1)<Input type="number" min="0" max="1" step="0.01" value={value.min_confidence} onChange={(event) => change("min_confidence", event.target.value)} /></label>
    {select("mismatch", "Neslaganje", FILTER_OPTIONS.mismatch)}
    {select("source", "Izvor", FILTER_OPTIONS.source)}
    {select("uploaded_by_id", "Unio", [["", "Svi korisnici"], ...uploaders.map(({ id, name }): [string, string] => [id, name])])}
    <AgencyBatchFilter value={value.batch_id} onChange={next => change("batch_id", next)} />
    <label className="space-y-1 text-sm">Datum od<Input type="date" value={value.uploaded_from} onChange={(event) => change("uploaded_from", event.target.value)} /></label>
    <label className="space-y-1 text-sm">Datum do<Input type="date" value={value.uploaded_to} onChange={(event) => change("uploaded_to", event.target.value)} /></label>
  </div>;
}
